// (c) chaehongee. Confidential and proprietary.

import type { ReactElement, CSSProperties } from 'react'
import { memo, useCallback, useEffect, useRef } from 'react'
import { Play } from 'lucide-react'
import styles from '../../Canvas.module.css'

interface VideoItemProps {
    src: string
    onLoadedMetadata?: (e: React.SyntheticEvent<HTMLVideoElement, Event>) => void
    onError?: () => void
    rect?: { x: number; y: number; w: number; h: number } | null
    frameClipRect?: { x: number; y: number; w: number; h: number }
    selected?: boolean
    readOnly?: boolean
    onPointerDown?: (e: React.PointerEvent) => void
    onContextMenu?: (e: React.MouseEvent) => void
    videoRef?: (el: HTMLVideoElement | null) => void
    autoPlay?: boolean
    playOnHover?: boolean
    showBadge?: boolean
}

export const VideoItem = memo(function VideoItem(props: VideoItemProps): ReactElement {
    const {
        src,
        onLoadedMetadata,
        onError,
        rect,
        frameClipRect,
        selected,
        readOnly = false,
        onPointerDown,
        onContextMenu,
        videoRef,
        autoPlay = false,
        playOnHover = true,
        showBadge = true,
    } = props
    const localRef = useRef<HTMLVideoElement | null>(null)

    const setRef = useCallback(
        (el: HTMLVideoElement | null) => {
            localRef.current = el
            videoRef?.(el)
        },
        [videoRef]
    )

    const playVideo = useCallback(() => {
        const video = localRef.current
        if (!video) return
        const result = video.play()
        if (result && typeof result.catch === 'function') {
            result.catch(() => {})
        }
    }, [])

    const handlePointerEnter = useCallback(() => {
        playVideo()
    }, [playVideo])

    const handlePointerLeave = useCallback(() => {
        const video = localRef.current
        if (!video) return
        video.pause()
        try {
            video.currentTime = 0
        } catch {
            // Ignore seek errors (e.g., not enough data buffered)
        }
    }, [])

    useEffect(() => {
        if (!autoPlay) return
        const video = localRef.current
        if (!video) return
        if (video.readyState >= 1) {
            playVideo()
            return
        }
        const handleMetadata = () => playVideo()
        video.addEventListener('loadedmetadata', handleMetadata, { once: true })
        return () => {
            video.removeEventListener('loadedmetadata', handleMetadata)
        }
    }, [autoPlay, src, playVideo])

    const commonProps = {
        src,
        muted: true,
        loop: true,
        playsInline: true,
        preload: 'metadata',
        draggable: false,
        referrerPolicy: 'no-referrer',
        onLoadedMetadata,
        onError,
        ref: setRef,
    } as const

    if (frameClipRect && rect) {
        const frameStyle: CSSProperties = {
            left: 0,
            top: 0,
            width: frameClipRect.w,
            height: frameClipRect.h,
        }
        const innerStyle: CSSProperties = {
            position: 'absolute',
            left: rect.x - frameClipRect.x,
            top: rect.y - frameClipRect.y,
            width: rect.w,
            height: rect.h,
        }
        return (
            <div
                className={styles.frameClip + (!selected ? ' ' + styles.worldItemOutline : '')}
                style={frameStyle}
            >
                <video
                    {...commonProps}
                    style={{
                        ...innerStyle,
                        objectFit: 'fill',
                        pointerEvents: readOnly ? (playOnHover ? 'auto' : 'none') : 'auto',
                        zIndex: 1,
                    }}
                    onPointerEnter={playOnHover ? handlePointerEnter : undefined}
                    onPointerLeave={playOnHover ? handlePointerLeave : undefined}
                    onPointerDown={onPointerDown}
                    onContextMenu={onContextMenu}
                />
                {showBadge && (
                    <span className={styles.videoBadge} aria-hidden="true">
                        <Play className={styles.videoBadgeIcon} aria-hidden="true" />
                    </span>
                )}
            </div>
        )
    }

    return (
        <div
            className={styles.videoItem}
            style={
                rect
                    ? {
                        position: 'absolute',
                        left: rect.x,
                        top: rect.y,
                        width: rect.w,
                        height: rect.h,
                    }
                    : { width: '100%', height: '100%' }
            }
        >
            <video
                {...commonProps}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: rect ? 'fill' : 'contain',
                    pointerEvents: readOnly && playOnHover ? 'auto' : 'none',
                    display: 'block',
                    zIndex: 1,
                }}
                onPointerEnter={playOnHover ? handlePointerEnter : undefined}
                onPointerLeave={playOnHover ? handlePointerLeave : undefined}
            />
            {showBadge && (
                <span className={styles.videoBadge} aria-hidden="true">
                    <Play className={styles.videoBadgeIcon} aria-hidden="true" />
                </span>
            )}
        </div>
    )
})

